import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  useNotes, useNoteMutations, useContacts, useContactMutations, useArtifacts, useArtifactMutations,
  artifactHref, useSaveProject, useScopeChanges, useScopeChangeMutations,
} from "@/hooks/useData";
import { useToast } from "@/context/ToastContext";
import { parseDate, shortDate, startOfDay, pct, isOverdue, isToday, thisWeekRange } from "@/lib/format";
import { buildTemplateCsv, downloadCsv } from "@/lib/templateCsv";
import { TYPE_META, STATUS_META, STATUS_ORDER, PROJECT_STATUS_LABEL, type Item, type ItemType, type ItemStatus, type Phase, type Project, type Subtask } from "@/lib/types";
import { buildHierarchy } from "@/lib/hierarchy";
import { Dot, StatusPill, ProgressBar, card } from "./atoms";
import { GanttTimeline } from "./GanttTimeline";
import { resolveClient, useClients, useTimeEntries } from "@/hooks/useTime";
import { breakdownText, budgetStatus, feeLabel, hours, inBounds, money, periodBounds, totalsOf, type Period } from "@/lib/timeRollup";
import { useCalendarFeedUrl, projectFeedUrl } from "@/lib/calendarFeed";
import { CopyableUrl, SubscribeHint } from "./CalendarFeedLink";
import { MonthCalendar, CalendarNavButtons, useCalendarNav } from "@/views/CalendarView";
import { ReportMasthead, reportPaper } from "./ReportMasthead";
import { KIND_LABEL, defaultKind, isFlatFee, isInternalProject, kindOf, projectRate } from "@/lib/billing";

const MS_DAY = 86_400_000;

const stepBtn: CSSProperties = {
  width: 24, height: 24, borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 13, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};

/**
 * Commercial terms.
 *
 * Every rate on every time entry and every figure in Reports resolves from
 * these nine fields, and until now the one page that defines a project showed
 * none of them. The burn line answers "how much of this is left" without a tab
 * change; the client row carries a tick when `clientRef` resolves, because a
 * project naming a client with no record silently costs it a rate and this is
 * where you would look first.
 */
function CommercialTerms({ project, mobile }: { project: Project; mobile: boolean }) {
  const { data: clients = [] } = useClients();
  const { data: entries = [] } = useTimeEntries();

  const client = resolveClient(project, clients);
  const rate = projectRate(project) ?? client?.defaultRate ?? null;
  const currency = project.currency || client?.currency || "USD";
  const status = budgetStatus(project, entries);

  const BILLING_LABEL: Record<string, string> = {
    none: "Not set (counts as hourly)", hourly: "Hourly", fixed: "Fixed fee", retainer: "Monthly retainer",
    internal: "Internal (your own work)",
  };

  const rows: [string, React.ReactNode][] = [
    ["Billing", BILLING_LABEL[project.billingType ?? "none"]],
    ["Category", project.category === "personal" ? "Personal" : "Work"],
    ["Rate", isInternalProject(project)
      ? <span style={{ color: "var(--muted)" }}>none — internal work is never charged</span>
      : isFlatFee(project)
      ? <span style={{ color: "var(--muted)" }}>not charged by the hour — covered by the {project.billingType === "retainer" ? "retainer" : "fee"}</span>
      : rate !== null
        ? <span>{money(rate, currency)}/h <span style={{ color: "var(--muted)" }}>· {projectRate(project) !== null ? "this project's rate" : `${client?.name ?? "client"} rate`}</span></span>
        : <span style={{ color: "var(--warn)" }}>none — hours bill nothing</span>],
    ["Currency", currency],
    ["New time logs as", KIND_LABEL[defaultKind(project, true)]],
    ["Client record", client
      ? <span style={{ color: "var(--good)" }}>✓ {client.name}</span>
      : <span style={{ color: "var(--warn)" }}>not linked</span>],
    ...(project.billingType === "fixed"
      ? ([
          ["Budgeted hours", project.budgetHours ? `${project.budgetHours} h` : "—"],
          ["Fee amount", project.feeAmount ? money(project.feeAmount, currency) : "—"],
        ] as [string, React.ReactNode][])
      : []),
    ...(project.billingType === "retainer"
      ? ([["Hours per month", project.retainerHours ? `${project.retainerHours} h` : "—"]] as [string, React.ReactNode][])
      : []),
  ];

  return (
    <div style={{ ...card, padding: mobile ? 14 : 18 }}>
      <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 12 }}>
        Commercial terms
      </div>
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "10px 24px" }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: "1px solid var(--tint2)", paddingBottom: 8 }}>
            <span style={{ fontSize: 12.5, color: "var(--muted)" }}>{k}</span>
            <span style={{ fontSize: 13, color: "var(--ink2)", textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>

      {status && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 6 }}>
            <span style={{ color: "var(--muted)" }}>
              {status.kind === "retainer" ? `This month · ${status.window}` : "Spent to date"}
            </span>
            <span className="mono" style={{ color: status.ratio > 1 ? "var(--bad)" : "var(--ink2)" }}>
              {hours(status.usedSeconds)} h of {status.budgetHours} spent
              {" · "}
              {(status.budgetHours - status.usedSeconds / 3600).toFixed(2)} h remaining
            </span>
          </div>
          <ProgressBar
            value={Math.min(100, status.ratio * 100)}
            color={status.ratio > 1 ? "var(--bad)" : status.ratio >= 0.85 ? "var(--warn)" : "var(--good)"}
          />
        </div>
      )}
    </div>
  );
}

// ---- Timeline --------------------------------------------------------------

export function TimelineTab({ phases, items, subCounts, onOpen, mobile }: { phases: Phase[]; items: Item[]; subCounts?: Map<string, { done: number; total: number }>; onOpen: (i: Item) => void; mobile: boolean }) {
  return <GanttTimeline phases={phases} items={items} subCounts={subCounts} onOpen={onOpen} mobile={mobile} />;
}

// ---- Project calendar ------------------------------------------------------

/**
 * The Calendar page's month grid and day panel, limited to this project's
 * items (already narrowed by the project's search and "show completed").
 */
export function ProjectCalendarTab({ items, projectId, mobile }: { items: Item[]; projectId: string; mobile: boolean }) {
  const nav = useCalendarNav();
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 16, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)" }}>{nav.monthLabel}</span>
        <div style={{ marginLeft: "auto" }}><CalendarNavButtons nav={nav} /></div>
      </div>
      <MonthCalendar items={items} nav={nav} mobile={mobile} projectId={projectId} />
    </div>
  );
}

// ---- Notes -----------------------------------------------------------------

export function NotesTab({ projectId, mobile }: { projectId: string; mobile: boolean }) {
  const { data: notes = [] } = useNotes(projectId);
  const { add, update, remove } = useNoteMutations(projectId);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ ...card, padding: mobile ? 12 : 16, marginBottom: 14 }}>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Jot a note — decisions, client asks, meeting takeaways…" style={{ width: "100%", minHeight: 70, border: "none", background: "none", resize: "vertical", fontSize: 13.5, outline: "none" }} />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={() => { if (draft.trim()) { add.mutate(draft.trim()); setDraft(""); } }} style={primaryBtn}>Add note</button>
        </div>
      </div>
      {notes.length === 0 ? <Empty>No notes yet.</Empty> : notes.map((n) => (
        <div key={n.id} style={{ ...card, padding: 14, marginBottom: 10, borderLeft: n.pinned ? "3px solid var(--accent)" : undefined }}>
          {editing === n.id ? (
            <textarea
              autoFocus
              defaultValue={n.body}
              onBlur={(e) => { setEditing(null); const v = e.target.value; if (v !== n.body) update.mutate({ id: n.id, body: v }); }}
              onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setEditing(null); } }}
              style={{ width: "100%", minHeight: 70, borderRadius: 8, border: "1px solid var(--accentLine)", background: "var(--surface)", padding: "7px 9px", resize: "vertical", fontSize: 13.5, fontFamily: "inherit", color: "var(--ink)" }}
            />
          ) : (
            <div
              onClick={() => setEditing(n.id)}
              title="Click to edit"
              className="prose"
              style={{ fontSize: 13.5, whiteSpace: "pre-wrap", cursor: "text" }}
            >{n.body}</div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
            <span className="mono" style={{ fontSize: 10.5, color: "var(--muted2)" }}>{shortDate(n.created)}</span>
            {/* Explicit target values, not toggles: a double dispatch then lands
                on the same result instead of cancelling itself out (§13). */}
            <button
              onClick={(e) => { e.stopPropagation(); update.mutate({ id: n.id, pinned: !n.pinned ? true : false }); }}
              style={linkBtn}
            >{n.pinned ? "Unpin" : "Pin"}</button>
            <button
              onClick={(e) => { e.stopPropagation(); remove.mutate(n.id); }}
              style={{ ...linkBtn, color: "var(--warn)" }}
            >Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- About -----------------------------------------------------------------

export function AboutTab({ project, phases, items, subtasks, onEdit, mobile, startEditing }: { project: Project; phases: Phase[]; items: Item[]; subtasks: Subtask[]; onEdit: () => void; mobile: boolean; startEditing?: boolean }) {
  const saveProject = useSaveProject();
  const { data: contacts = [] } = useContacts(project.id);
  const { data: artifacts = [] } = useArtifacts(project.id);
  const { add: addArtifact, upload: uploadArtifact, update: updateArtifact, remove: removeArtifact } = useArtifactMutations(project.id);
  const { add: addContact, update: updateContact, remove: removeContact } = useContactMutations(project.id);
  const [attachOpen, setAttachOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null); // kind awaiting a URL
  const [pendingUrl, setPendingUrl] = useState("");

  // §13 — new rows PREPEND. Appending to the bottom of a scrolled list reads as
  // a dead button.
  const topPosition = () => (artifacts.length ? Math.min(...artifacts.map((a) => a.position ?? 0)) - 1 : 0);

  function attachLink(kind: string) {
    setAttachOpen(false);
    setPending(kind);
    setPendingUrl("");
  }

  function commitLink() {
    const url = pendingUrl.trim();
    if (!url || !pending) { setPending(null); return; }
    addArtifact.mutate({ label: url.replace(/^https?:\/\//, "").slice(0, 60), kind: pending, driveUrl: url, position: topPosition() });
    setPending(null);
    setPendingUrl("");
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    // Keep uploads modest; the demo stores files inline in localStorage.
    const MAX = 5 * 1024 * 1024;
    if (file.size > MAX) { window.alert("Please choose a file under 5 MB."); return; }
    uploadArtifact.mutate({ label: file.name, kind: "Doc", file, position: topPosition() });
  }

  function exportTemplate() {
    const csv = buildTemplateCsv(project, phases, items, subtasks.filter((s) => items.some((i) => i.id === s.item)));
    const slug = project.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    downloadCsv(`${slug || "project"}-template.csv`, csv);
  }

  const rows: [string, string][] = [
    ["Client", project.client || "—"],
    ["Status", PROJECT_STATUS_LABEL[project.status]],
    ["Start", shortDate(project.start) || "—"],
    ["Due", shortDate(project.due) || "—"],
    ["Fee", feeLabel(project) || "—"],
    ["Contract", project.contract || "—"],
  ];
  return (
    <div style={{ maxWidth: 720, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ ...card, padding: mobile ? 14 : 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Details</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={exportTemplate} style={ghostBtn} title="Save this project as a reusable CSV template with relative dates">Export as template</button>
            <button onClick={onEdit} style={ghostBtn}>Edit</button>
          </div>
        </div>
        <NameRow
          project={project}
          autoFocus={startEditing}
          onCommit={(name) => { if (name && name !== project.name) saveProject.mutate({ id: project.id, name }); }}
        />
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "10px 24px" }}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: "1px solid var(--tint2)", paddingBottom: 8 }}>
              <span style={{ fontSize: 12.5, color: "var(--muted)" }}>{k}</span>
              <span style={{ fontSize: 13, color: "var(--ink2)", textTransform: k === "Status" ? "capitalize" : "none" }}>{v}</span>
            </div>
          ))}
        </div>
        {project.scope && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 4 }}>Scope</div>
            <div className="prose" style={{ fontSize: 13.5, color: "var(--ink2)" }}>{project.scope}</div>
          </div>
        )}
      </div>

      <CommercialTerms project={project} mobile={mobile} />

      <div style={{ ...card, padding: mobile ? 14 : 18 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Contacts</span>
          <button onClick={() => addContact.mutate(contacts.length)} style={{ ...ghostBtn, marginLeft: "auto" }}>+ Add</button>
        </div>
        {contacts.length === 0 ? <span style={{ fontSize: 13, color: "var(--muted2)" }}>No contacts yet.</span> : contacts.map((c) => (
          <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid var(--tint2)" }}>
            <InlineText value={c.name} placeholder="Name" bold onCommit={(v) => v !== c.name && updateContact.mutate({ id: c.id, name: v })} />
            <InlineText value={c.role} placeholder="Role" muted onCommit={(v) => v !== c.role && updateContact.mutate({ id: c.id, role: v })} />
            <InlineText value={c.email} placeholder="Email" accent onCommit={(v) => v !== c.email && updateContact.mutate({ id: c.id, email: v })} />
            <button onClick={() => removeContact.mutate(c.id)} style={{ ...linkBtn, color: "var(--warn)", flex: "none" }} aria-label={`Remove ${c.name || "contact"}`}>Remove</button>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: mobile ? 14 : 18 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10, position: "relative" }}>
          <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Files</span>
          <button onClick={() => setAttachOpen((v) => !v)} style={{ ...ghostBtn, marginLeft: "auto" }}>+ Attach ▾</button>
          {attachOpen && (
            <>
              <div onClick={() => setAttachOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 20 }} />
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 21, width: 210, ...card, padding: 5, boxShadow: "var(--shadowHover)" }}>
                {([["Doc", "Google Doc"], ["Sheet", "Google Sheet"], ["Slides", "Google Slides"]] as [string, string][]).map(([kind, label]) => (
                  <button key={kind} onClick={() => attachLink(kind)} style={menuItemStyle}>{label}</button>
                ))}
                <label style={{ ...menuItemStyle, display: "block", cursor: "pointer" }}>
                  Upload from device
                  <input type="file" onChange={(e) => { setAttachOpen(false); onPickFile(e); }} style={{ display: "none" }} />
                </label>
              </div>
            </>
          )}
        </div>
        {pending && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid var(--tint2)" }}>
            <span className="mono" style={{ fontSize: 10, padding: "2px 6px", borderRadius: 5, background: "var(--tint)", color: "var(--ink3)", flex: "none" }}>{pending}</span>
            <input
              autoFocus
              value={pendingUrl}
              onChange={(e) => setPendingUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitLink(); } if (e.key === "Escape") setPending(null); }}
              placeholder="Paste the link…"
              style={{ flex: 1, minWidth: 0, padding: "5px 8px", borderRadius: 7, border: "1px solid var(--accentLine)", background: "var(--surface)", fontSize: 13, color: "var(--ink)" }}
            />
            <button onClick={commitLink} style={{ ...ghostBtn, flex: "none" }}>Add</button>
            <button onClick={() => setPending(null)} style={{ ...linkBtn, flex: "none", color: "var(--muted)" }}>Cancel</button>
          </div>
        )}
        {artifacts.length === 0 && !pending ? <span style={{ fontSize: 13, color: "var(--muted2)" }}>No linked files yet.</span> : artifacts.map((a) => {
          const href = artifactHref(a);
          const isUpload = !!(a.file || a.fileData);
          const dl = a.fileName || a.file;
          return (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid var(--tint2)" }}>
              <span className="mono" style={{ fontSize: 10, padding: "2px 6px", borderRadius: 5, background: "var(--tint)", color: "var(--ink3)", flex: "none" }}>{isUpload ? "File" : a.kind}</span>
              <InlineText value={a.label} placeholder="File name" onCommit={(v) => v && v !== a.label && updateArtifact.mutate({ id: a.id, label: v })} />
              {href && (
                <a href={href} target="_blank" rel="noreferrer" {...(isUpload ? { download: dl } : {})} title="Open" style={{ flex: "none", fontSize: 13, color: "var(--accent)", textDecoration: "none" }}>↗</a>
              )}
              <button onClick={() => removeArtifact.mutate(a.id)} style={{ ...linkBtn, color: "var(--warn)", flex: "none" }} aria-label={`Remove ${a.label}`}>Remove</button>
            </div>
          );
        })}
      </div>

      <ScopeChangesCard projectId={project.id} mobile={mobile} />
      <CalendarFeedCard projectId={project.id} mobile={mobile} />
    </div>
  );
}

/**
 * This project's own calendar subscription.
 *
 * Rendered only when the Worker hands back a feed URL, so the card is absent
 * rather than dead when the feed isn't configured. Settings says why.
 */
function CalendarFeedCard({ projectId, mobile }: { projectId: string; mobile: boolean }) {
  const { data: feed } = useCalendarFeedUrl();
  const feedUrl = feed?.url;
  // Settings carries the explanation when the feed is unavailable; repeating it
  // on every project would be noise.
  if (!feedUrl) return null;

  return (
    <div style={{ ...card, padding: mobile ? 14 : 18 }}>
      <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 4 }}>
        Calendar subscription
      </div>
      <div style={{ fontSize: 12.5, color: "var(--ink3)", marginBottom: 12 }}>
        This project's dated items, as their own calendar. Subscribe to it instead of
        the all-projects feed in Settings — both at once shows every date twice.
      </div>
      <CopyableUrl url={projectFeedUrl(feedUrl, projectId)} />
      <SubscribeHint />
    </div>
  );
}

/**
 * §13 — a dated record of what moved and what it cost. This is the artifact
 * that justifies a change order, so every field stays editable after the fact.
 */
function ScopeChangesCard({ projectId, mobile }: { projectId: string; mobile: boolean }) {
  const { data: changes = [] } = useScopeChanges(projectId);
  const { add, update, remove } = useScopeChangeMutations(projectId);

  return (
    <div style={{ ...card, padding: mobile ? 14 : 18 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Scope changes</span>
        <button onClick={() => add.mutate(new Date().toISOString().slice(0, 10))} style={{ ...ghostBtn, marginLeft: "auto" }}>+ Log a change</button>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--ink3)", marginBottom: 12 }}>
        A dated record of what moved and what it cost. This is the artifact that justifies a change order.
      </div>

      {changes.length === 0 ? (
        <span style={{ fontSize: 13, color: "var(--muted2)" }}>No scope changes logged. Original scope stands.</span>
      ) : changes.map((c) => (
        <div key={c.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--tint2)" }}>
          <input
            type="date"
            defaultValue={(c.date || "").slice(0, 10)}
            onBlur={(e) => e.target.value !== (c.date || "").slice(0, 10) && update.mutate({ id: c.id, date: e.target.value })}
            className="mono"
            style={{ width: 62, flex: "none", padding: 0, border: "none", background: "none", fontSize: 11.5, color: "var(--muted)" }}
          />
          <textarea
            defaultValue={c.body}
            placeholder="What moved?"
            onBlur={(e) => e.target.value !== c.body && update.mutate({ id: c.id, body: e.target.value })}
            style={{ flex: 1, minWidth: 0, minHeight: 38, padding: "5px 8px", borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 13, fontFamily: "inherit", color: "var(--ink2)", resize: "vertical" }}
          />
          <input
            defaultValue={c.impact}
            placeholder="+$0"
            onBlur={(e) => e.target.value !== c.impact && update.mutate({ id: c.id, impact: e.target.value })}
            className="mono"
            style={{ width: 108, flex: "none", padding: "5px 8px", borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 12.5, color: "var(--ink2)", textAlign: "right" }}
          />
          <button onClick={() => remove.mutate(c.id)} style={{ ...linkBtn, color: "var(--muted2)", flex: "none", fontSize: 13 }} aria-label="Remove scope change">✕</button>
        </div>
      ))}
    </div>
  );
}

/** A field that looks like text until you click it. */
function InlineText({ value, placeholder, onCommit, bold, muted, accent }: {
  value: string; placeholder: string; onCommit: (v: string) => void;
  bold?: boolean; muted?: boolean; accent?: boolean;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onCommit(e.currentTarget.value.trim()); e.currentTarget.blur(); }
        if (e.key === "Escape") { setV(value); e.currentTarget.blur(); }
      }}
      onBlur={() => onCommit(v.trim())}
      style={{
        flex: 1, minWidth: 0, padding: "4px 6px", borderRadius: 7,
        border: "1px solid transparent", background: "transparent",
        fontSize: bold ? 13 : 12.5, fontWeight: bold ? 500 : 400,
        color: accent ? "var(--accent)" : muted ? "var(--muted)" : "var(--ink2)",
      }}
      onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accentLine)"; e.currentTarget.style.background = "var(--surface)"; }}
      onBlurCapture={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; }}
    />
  );
}

const menuItemStyle: React.CSSProperties = {
  width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8,
  border: "none", background: "transparent", cursor: "pointer",
  fontSize: 13, color: "var(--ink2)",
};

// ---- Client / executive report ---------------------------------------------

const REPORT_PERIODS: [Period, string][] = [
  ["month", "Month"], ["week", "Week"], ["all", "Whole engagement"],
];

export function ClientReportTab({ project, phases, items, mobile }: { project: Project; phases: Phase[]; items: Item[]; mobile: boolean }) {
  const toast = useToast();
  const { data: allEntries = [] } = useTimeEntries();
  const { data: clients = [] } = useClients();

  // The billing period scopes the TIME figures only. The item lists below are
  // status-based, not period-based, so scoping them here would quietly change
  // what the report has always shown. The section says which period it covers.
  const [period, setPeriod] = useState<Period>("month");
  const [offset, setOffset] = useState(0);
  const bounds = useMemo(() => periodBounds(period, offset), [period, offset]);

  const entries = useMemo(
    // Client-facing: Internal time logged against the project (the proposal,
    // say) is yours, not theirs, so it stays off this report.
    () => allEntries.filter((e) => e.project === project.id && inBounds(e, bounds) && kindOf(e, project) !== "internal"),
    [allEntries, project, bounds],
  );
  const totals = useMemo(() => totalsOf(entries, [project]), [entries, project]);

  /** Hours per task, so the client can see where the time actually went. */
  const byTask = useMemo(() => {
    const acc = new Map<string, number>();
    for (const e of entries) {
      const key = e.item || "__project";
      acc.set(key, (acc.get(key) ?? 0) + (e.durationSeconds || 0));
    }
    return [...acc]
      .map(([id, seconds]) => ({
        seconds,
        label: id === "__project"
          ? "General project work"
          : items.find((i) => i.id === id)?.title ?? "Removed task",
      }))
      .sort((a, b) => b.seconds - a.seconds);
  }, [entries, items]);

  const rateNote = useMemo(() => {
    const client = clients.find((c) => c.id === project.clientRef)
      ?? clients.find((c) => c.name.trim().toLowerCase() === (project.client ?? "").trim().toLowerCase());
    const rate = projectRate(project) ?? client?.defaultRate ?? null;
    return rate ? `${money(rate, totals.currency)}/h` : null;
  }, [clients, project, totals.currency]);

  const visible = items.filter((i) => i.clientVisible);
  const done = visible.filter((i) => i.status === "done").length;
  const accomplishments = visible.filter((i) => i.status === "done");
  const active = visible.filter((i) => i.status !== "done");
  const activeDeliverables = active
    .filter((i) => i.type === "deliverable" || i.type === "milestone")
    .sort((a, b) => (a.due || "9999").localeCompare(b.due || "9999"));
  const upcomingMilestones = visible
    .filter((i) => i.type === "milestone" && i.status !== "done" && i.due)
    .sort((a, b) => (a.due || "").localeCompare(b.due || ""));

  /** Plain-text deliverable list, straight to the clipboard. Nothing leaves the machine. */
  async function copySummary() {
    const lines = [
      project.name,
      project.client ? `Client: ${project.client}` : "",
      "",
      ...activeDeliverables.map((i) => `- ${i.title}${i.due ? ` (due ${shortDate(i.due)})` : ""} — ${STATUS_META[i.status].label}`),
      "",
      `${done} of ${visible.length} client-visible items complete.`,
      ...(totals.seconds > 0
        ? [
            "",
            `Time — ${bounds.label}`,
            `${hours(totals.seconds)} h logged (${breakdownText(totals, hours)})`,
            ...(totals.amount > 0 ? [`Amount: ${money(totals.amount, totals.currency)}`] : []),
          ]
        : []),
    ].filter((l) => l !== undefined);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast("Summary copied");
    } catch {
      toast("Could not copy — clipboard blocked");
    }
  }

  /**
   * §16 — a placeholder. There is no view-only client URL yet, so this copies
   * the current address and says so rather than implying a share happened.
   */
  async function shareLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast("Link copied — view-only sharing isn't live yet");
    } catch {
      toast("Could not copy — clipboard blocked");
    }
  }

  const reportRow = (it: Item, opts: { strike?: boolean } = {}) => (
    <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--tint2)" }}>
      <span style={{ color: TYPE_META[it.type].color, fontSize: 12, flex: "none" }}>{TYPE_META[it.type].glyph}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, textDecoration: opts.strike ? "line-through" : "none", color: opts.strike ? "var(--muted)" : "var(--ink)" }}>{it.title}</span>
      <span style={{ padding: "2px 9px", borderRadius: 20, fontSize: 11, background: STATUS_META[it.status].bg, color: STATUS_META[it.status].text, flex: "none" }}>{STATUS_META[it.status].label}</span>
      {it.due && <span className="mono" style={{ fontSize: 11, color: "var(--ink3)", width: 54, textAlign: "right", flex: "none" }}>{shortDate(it.due)}</span>}
    </div>
  );

  const sectionHead = (label: string, count: number) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "22px 0 8px" }}>
      <span style={{ fontSize: 15, fontWeight: 500 }}>{label}</span>
      <span className="mono" style={{ fontSize: 11, color: "var(--muted2)" }}>{count}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: 800 }}>
      <div className="no-print" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontSize: 13, color: "var(--ink3)" }}>
          Executive readout — shows only items marked <b>“On the client report.”</b> {visible.length} of {items.length} items are client-visible.
        </span>
        <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={copySummary} style={ghostBtn}>Copy summary</button>
          <button onClick={shareLink} style={ghostBtn}>Share link</button>
          {/* The print view paginates the screen layout — a typeset PDF is a
              separate piece of work, not this button. */}
          <button onClick={() => window.print()} style={primaryBtn}>Print / Save PDF</button>
        </span>
      </div>

      <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Billing period</span>
        <div style={{ display: "flex", padding: 3, background: "var(--tint)", borderRadius: 999 }}>
          {REPORT_PERIODS.map(([k, label]) => {
            const on = k === period;
            return (
              <button key={k} onClick={() => { setPeriod(k); setOffset(0); }} style={{
                padding: "5px 12px", borderRadius: 999, fontSize: 12, cursor: "pointer", border: "none",
                background: on ? "var(--surface)" : "transparent",
                color: on ? "var(--accent)" : "var(--ink3)",
                fontWeight: on ? "var(--fw-title)" : "var(--fw-medium)",
                boxShadow: on ? "var(--shadowCard)" : "none",
              }}>{label}</button>
            );
          })}
        </div>
        {period !== "all" && (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={() => setOffset((o) => o - 1)} style={stepBtn} aria-label="Previous period">‹</button>
            <span style={{ fontSize: 12, color: "var(--ink3)", minWidth: 118, textAlign: "center" }}>{bounds.label}</span>
            <button
              onClick={() => setOffset((o) => Math.min(0, o + 1))}
              disabled={bounds.isCurrent} aria-label="Next period"
              style={{ ...stepBtn, opacity: bounds.isCurrent ? 0.35 : 1, cursor: bounds.isCurrent ? "default" : "pointer" }}
            >›</button>
          </span>
        )}
      </div>

      <div className="print-page" style={{ ...card, ...(mobile ? { padding: 18 } : reportPaper) }}>
        {/* The shared letterhead: logo (or business name) top-left, as on
            the timesheet and the expense package. */}
        <ReportMasthead
          kind="Executive report"
          title={project.name}
          subtitle={project.client || undefined}
          meta={[["Prepared", new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })]]}
        />

        {/* Summary band */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--ink3)" }}>{done} of {visible.length} client deliverables complete</span>
          <div style={{ flex: 1, minWidth: 160, maxWidth: 280 }}><ProgressBar value={pct(done, visible.length)} color={project.color} /></div>
          <span className="mono" style={{ fontSize: 13, fontWeight: 500 }}>{pct(done, visible.length)}%</span>
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12.5, color: "var(--ink3)" }}>
          {project.due && <span>Target: <b>{shortDate(project.due)}</b></span>}
          {feeLabel(project) && <span>Fee: <b>{feeLabel(project)}</b></span>}
          <span>{accomplishments.length} complete · {active.length} in flight</span>
        </div>

        <div style={{
          display: "flex", gap: 22, flexWrap: "wrap", alignItems: "baseline",
          marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--tint2)",
        }}>
          <span style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {period === "all" ? "Whole engagement" : bounds.label}
          </span>
          {totals.seconds === 0 ? (
            <span style={{ fontSize: 12.5, color: "var(--muted)" }}>No time logged in this period.</span>
          ) : (
            <>
              <span style={{ fontSize: 12.5, color: "var(--ink3)" }}>
                <b className="mono" style={{ fontSize: 15, color: "var(--ink)" }}>{hours(totals.seconds)}</b> hours
              </span>
              <span style={{ fontSize: 12.5, color: "var(--ink3)" }}>
                {breakdownText(totals, hours)}
              </span>
              {totals.amount > 0 && (
                <span style={{ fontSize: 12.5, color: "var(--ink3)" }}>
                  <b className="mono" style={{ fontSize: 15, color: "var(--ink)" }}>{money(totals.amount, totals.currency)}</b>
                  {rateNote && <span style={{ color: "var(--muted2)" }}> at {rateNote}</span>}
                </span>
              )}
              {totals.amount === 0 && totals.billableSeconds > 0 && (
                <span style={{ fontSize: 12, color: "var(--warn)" }}>
                  Billable hours with no rate — set one on the client.
                </span>
              )}
            </>
          )}
        </div>

        {visible.length === 0 ? (
          <div style={{ marginTop: 18 }}><Empty>No client-visible items yet. Toggle “On the client report” on the items you want to share.</Empty></div>
        ) : (
          <>
            {upcomingMilestones.length > 0 && (
              <>
                {sectionHead("Upcoming milestones", upcomingMilestones.length)}
                {upcomingMilestones.map((it) => reportRow(it))}
              </>
            )}

            {activeDeliverables.length > 0 && (
              <>
                {sectionHead("Active deliverables", activeDeliverables.length)}
                {activeDeliverables.map((it) => reportRow(it))}
              </>
            )}

            {accomplishments.length > 0 && (
              <>
                {sectionHead("Accomplishments", accomplishments.length)}
                {accomplishments.map((it) => reportRow(it, { strike: true }))}
              </>
            )}

            {byTask.length > 0 && (
              <>
                {sectionHead(`Where the time went — ${bounds.label}`, byTask.length)}
                {byTask.map((row) => (
                  <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--tint2)" }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>{row.label}</span>
                    <span className="mono" style={{ fontSize: 12.5, color: "var(--ink3)", flex: "none" }}>{hours(row.seconds)} h</span>
                  </div>
                ))}
              </>
            )}

            {/* Full detail, grouped by phase (unchanged readout, kept for the print export) */}
            {sectionHead("Detail by phase", visible.length)}
            {phases.map((ph) => {
              const rows = visible.filter((i) => i.phase === ph.id);
              if (rows.length === 0) return null;
              return (
                <div key={ph.id} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 500, margin: "10px 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
                    <Dot color={ph.color} />{ph.name}
                  </div>
                  {rows.map((it) => reportRow(it, { strike: it.status === "done" }))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// ---- Analytics -------------------------------------------------------------

export function AnalyticsTab({ project, phases, items, onOpen, mobile }: { project: Project; phases: Phase[]; items: Item[]; onOpen: (i: Item) => void; mobile: boolean }) {
  void project;
  const total = items.length;
  const done = items.filter((i) => i.status === "done").length;
  const overdue = items.filter((i) => isOverdue(i)).length;
  const waitingBlocked = items.filter((i) => i.status === "waiting" || i.status === "blocked").length;
  const [twStart, twEnd] = thisWeekRange();
  const dueThisWeek = items.filter((i) => { if (i.status === "done") return false; const d = parseDate(i.due); return !!d && d >= twStart && d < twEnd; }).length;

  const today = startOfDay().getTime();
  let oldestLate = 0;
  for (const i of items) { if (isOverdue(i)) { const d = parseDate(i.due); if (d) oldestLate = Math.max(oldestLate, Math.round((today - startOfDay(d).getTime()) / 86400000)); } }

  const statusCounts = STATUS_ORDER.map((s) => ({ s, n: items.filter((i) => i.status === s).length }));
  const typeCounts = (["task", "deliverable", "milestone"] as ItemType[]).map((t) => ({ t, n: items.filter((i) => i.type === t).length }));

  if (total === 0) return <Empty>No items yet — analytics will populate as you add work.</Empty>;

  // Status donut via conic-gradient.
  let acc = 0;
  const stops: string[] = [];
  for (const { s, n } of statusCounts) {
    if (n === 0) continue;
    const from = (acc / total) * 100;
    acc += n;
    const to = (acc / total) * 100;
    stops.push(`${STATUS_META[s].dot} ${from}% ${to}%`);
  }
  const donut = stops.length ? `conic-gradient(${stops.join(", ")})` : "var(--tint2)";

  // Completions against plan: items in due order; planned ramps linearly to
  // the total, completed is the running count of done items.
  const dueOrder = [...items].filter((i) => i.due).sort((a, b) => (a.due! < b.due! ? -1 : 1));
  const N = dueOrder.length;
  const W = 400, H = 150, PAD = 6;
  const px = (i: number) => PAD + (N <= 1 ? 0 : (i / (N - 1)) * (W - 2 * PAD));
  const py = (v: number) => H - PAD - (total === 0 ? 0 : (v / total) * (H - 2 * PAD));
  let run = 0;
  const planned: string[] = [], actual: string[] = [];
  dueOrder.forEach((it, i) => {
    planned.push(`${px(i)},${py(((i + 1) / N) * total)}`);
    if (it.status === "done") run++;
    actual.push(`${px(i)},${py(run)}`);
  });

  const stat = (label: string, value: string | number, ctx: string, warn?: boolean) => (
    <div style={{ ...card, padding: "16px 18px" }}>
      <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{label}</div>
      <div className="mono" style={{ fontSize: 28, fontWeight: "var(--fw-medium)", color: warn ? "var(--bad)" : "var(--ink)", marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: "var(--muted2)", marginTop: 4 }}>{ctx}</div>
    </div>
  );

  const attention = [
    ...items.filter((i) => isOverdue(i)),
    ...items.filter((i) => !isOverdue(i) && (i.status === "waiting" || i.status === "blocked")),
  ];

  return (
    <div style={{ maxWidth: 980, display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10 }}>
        {stat("Complete", `${pct(done, total)}%`, `${done} of ${total} items`)}
        {stat("Overdue", overdue, overdue > 0 ? `oldest ${oldestLate} day${oldestLate === 1 ? "" : "s"} late` : "nothing late", overdue > 0)}
        {stat("Due this week", dueThisWeek, "next seven days")}
        {stat("Waiting or blocked", waitingBlocked, "needs a nudge", waitingBlocked > 0)}
      </div>

      {/* Donut + burn-up */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(0,1fr) minmax(0,1.3fr)", gap: 10 }}>
        <div style={{ ...card, padding: mobile ? 14 : 18 }}>
          <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 14 }}>Work by status</div>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <div style={{ position: "relative", width: 132, height: 132, borderRadius: "50%", background: donut, flex: "none" }}>
              <div style={{ position: "absolute", inset: 23, borderRadius: "50%", background: "var(--surface)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span className="mono" style={{ fontSize: 22, fontWeight: "var(--fw-medium)" }}>{total}</span>
                <span style={{ fontSize: 9.5, letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--muted2)" }}>Items</span>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 150, flex: 1 }}>
              {statusCounts.map(({ s, n }) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_META[s].dot, flex: "none" }} />
                  <span style={{ color: "var(--ink2)" }}>{STATUS_META[s].label}</span>
                  <span className="mono" style={{ marginLeft: "auto", color: "var(--ink3)" }}>{n}</span>
                  <span className="mono" style={{ width: 38, textAlign: "right", color: "var(--muted2)" }}>{pct(n, total)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ ...card, padding: mobile ? 14 : 18 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>Completions against plan</span>
            <span style={{ fontSize: 11.5, color: "var(--muted2)" }}>cumulative, ordered by due date</span>
          </div>
          {N === 0 ? (
            <span style={{ fontSize: 13, color: "var(--muted2)" }}>No dated items yet.</span>
          ) : (
            <>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} preserveAspectRatio="none">
                <polyline points={planned.join(" ")} fill="none" stroke="var(--muted3)" strokeWidth={2} strokeDasharray="5 5" />
                <polyline points={actual.join(" ")} fill="none" stroke="var(--accent)" strokeWidth={3} strokeLinejoin="round" />
              </svg>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--ink3)" }}><span style={{ width: 16, height: 3, background: "var(--accent)" }} />Completed</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--ink3)" }}><span style={{ width: 16, height: 0, borderTop: "2px dashed var(--muted3)" }} />Planned</span>
                <span style={{ marginLeft: "auto", fontSize: 11.5, color: overdue > 0 ? "var(--bad)" : "var(--good)" }}>{overdue > 0 ? `${overdue} behind plan` : "on or ahead of plan"}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Progress by phase + by type */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 10 }}>
        <div style={{ ...card, padding: mobile ? 14 : 18 }}>
          <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 12 }}>Progress by phase</div>
          {phases.length === 0 ? <span style={{ fontSize: 13, color: "var(--muted2)" }}>No phases yet.</span> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {phases.map((ph) => {
                const list = items.filter((i) => i.phase === ph.id);
                const d = list.filter((i) => i.status === "done").length;
                return (
                  <div key={ph.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <Dot color={ph.color} />
                      <span style={{ fontSize: 13, fontWeight: "var(--fw-medium)" }}>{ph.name}</span>
                      <span className="mono" style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--muted)" }}>{d}/{list.length} · {pct(d, list.length)}%</span>
                    </div>
                    <ProgressBar value={pct(d, list.length)} color={ph.color} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ ...card, padding: mobile ? 14 : 18 }}>
          <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 12 }}>By type</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {typeCounts.map(({ t, n }) => (
              <div key={t}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ color: TYPE_META[t].color, fontSize: 12 }}>{TYPE_META[t].glyph}</span>
                  <span style={{ fontSize: 13, color: "var(--ink2)" }}>{TYPE_META[t].label}</span>
                  <span className="mono" style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)" }}>{n}</span>
                </div>
                <ProgressBar value={total ? (n / total) * 100 : 0} color={TYPE_META[t].color === "var(--muted)" ? "var(--ink3)" : TYPE_META[t].color} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attention */}
      <div style={{ ...card, padding: mobile ? 14 : 18 }}>
        <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", marginBottom: 12 }}>Attention</div>
        {attention.length === 0 ? (
          <span style={{ fontSize: 13, color: "var(--muted2)" }}>Nothing needs attention right now.</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {attention.map((it) => {
              const late = isOverdue(it);
              const phase = phases.find((p) => p.id === it.phase);
              return (
                <div key={it.id} onClick={() => onOpen(it)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, background: late ? "var(--badTint)" : "var(--warnTint)", cursor: "pointer" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: late ? "var(--bad)" : "var(--warn)", flex: "none" }} />
                  <span style={{ fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--ink3)", flex: "none" }}>{STATUS_META[it.status].label}{phase ? ` · ${phase.name}` : ""}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Deliverables matrix ---------------------------------------------------

// Short column labels so the matrix header stays one line per status
// ("Waiting", not "Waiting on someone", which wraps and misaligns the row).
const SHORT_STATUS: Record<ItemStatus, string> = {
  todo: "Not started",
  doing: "In progress",
  waiting: "Waiting",
  blocked: "Blocked",
  done: "Completed",
};

// ---- Structure (§9) --------------------------------------------------------

/**
 * A phase-by-phase outline: each phase lists its deliverables and milestones,
 * with their tasks indented beneath.
 *
 * Two rules worth stating, because both are easy to get wrong:
 *  - Tasks with no parent are NOT hidden. They get an "Unassigned tasks" group
 *    at the foot of their phase; working straight in a phase is normal.
 *  - A task whose parent sits in a different phase still nests under that
 *    parent, and carries a chip naming its own phase — so it appears once, in
 *    the place that explains it, without lying about where it lives.
 */
export function StructureTab({ phases, items, onOpen, mobile }: {
  phases: Phase[]; items: Item[]; onOpen: (i: Item) => void; mobile: boolean;
}) {
  const { parentOf, childrenOf } = useMemo(() => buildHierarchy(items), [items]);

  if (phases.length === 0) return <Empty>No phases yet.</Empty>;

  return (
    <div style={{ maxWidth: 900 }}>
      {phases.map((ph) => {
        const mine = items.filter((i) => i.phase === ph.id);
        const outputs = mine.filter((i) => i.type !== "task");
        const unassigned = mine.filter((i) => i.type === "task" && !parentOf(i));
        const done = mine.filter((i) => i.status === "done").length;

        return (
          <div key={ph.id} style={{ ...card, padding: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: outputs.length || unassigned.length ? 10 : 0 }}>
              <Dot color={ph.color} size={8} />
              <span style={{ fontSize: 14, fontWeight: "var(--fw-title)" }}>{ph.name}</span>
              {ph.description && (
                <span style={{ fontSize: 12.5, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ph.description}
                </span>
              )}
              <span className="mono" style={{ marginLeft: "auto", flex: "none", fontSize: 11.5, color: "var(--muted2)" }}>
                {done}/{mine.length}
              </span>
            </div>

            {outputs.length === 0 && unassigned.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--muted2)", paddingLeft: 16 }}>Nothing in this phase yet.</div>
            ) : (
              <>
                {outputs.map((o) => {
                  const kids = childrenOf(o);
                  const kidsDone = kids.filter((k) => k.status === "done").length;
                  return (
                    <div key={o.id}>
                      <button onClick={() => onOpen(o)} style={outputRow(mobile)}>
                        <span style={{ color: TYPE_META[o.type].color, flex: "none" }}>{TYPE_META[o.type].glyph}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: "var(--fw-title)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
                          {o.title}
                        </span>
                        {kids.length > 0 && (
                          <span style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
                            <span style={{ width: 54 }}><ProgressBar value={pct(kidsDone, kids.length)} color={ph.color} /></span>
                            <span className="mono" style={{ fontSize: 10.5, color: "var(--muted2)" }}>{kidsDone}/{kids.length}</span>
                          </span>
                        )}
                        <StatusPill status={o.status} />
                        <span className="mono" style={{ flex: "none", width: 52, textAlign: "right", fontSize: 11, color: isOverdue(o) ? "var(--bad)" : "var(--muted2)" }}>
                          {o.due ? shortDate(o.due) : "—"}
                        </span>
                      </button>
                      {kids.map((t) => (
                        <TaskLine key={t.id} task={t} onOpen={onOpen} elsewhere={t.phase !== ph.id ? phases.find((x) => x.id === t.phase)?.name : undefined} />
                      ))}
                    </div>
                  );
                })}

                {unassigned.length > 0 && (
                  <div style={{ marginTop: outputs.length ? 10 : 0 }}>
                    <div style={{ paddingLeft: 16, fontSize: 10, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--muted3)", marginBottom: 2 }}>
                      Unassigned tasks
                    </div>
                    {unassigned.map((t) => <TaskLine key={t.id} task={t} onOpen={onOpen} />)}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TaskLine({ task, onOpen, elsewhere }: { task: Item; onOpen: (i: Item) => void; elsewhere?: string }) {
  return (
    <button onClick={() => onOpen(task)} style={taskRow}>
      <span style={{ flex: "none", color: "var(--muted3)" }}>·</span>
      <span style={{
        flex: 1, minWidth: 0, fontSize: 13, fontWeight: "var(--fw-medium)", textAlign: "left",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        textDecoration: task.status === "done" ? "line-through" : "none",
        color: task.status === "done" ? "var(--muted)" : "var(--ink2)",
      }}>{task.title}</span>
      {elsewhere && (
        <span style={{ flex: "none", padding: "1px 7px", borderRadius: 999, border: "1px solid var(--line2)", fontSize: 10.5, color: "var(--muted)" }}>
          {elsewhere}
        </span>
      )}
      <StatusPill status={task.status} />
      <span className="mono" style={{ flex: "none", width: 52, textAlign: "right", fontSize: 11, color: isOverdue(task) ? "var(--bad)" : "var(--muted2)" }}>
        {task.due ? shortDate(task.due) : "—"}
      </span>
    </button>
  );
}

const outputRow = (mobile: boolean): CSSProperties => ({
  display: "flex", alignItems: "center", gap: 9, width: "100%",
  paddingLeft: 16, paddingRight: mobile ? 0 : 4, paddingTop: 6, paddingBottom: 6,
  border: "none", background: "none", cursor: "pointer",
});

const taskRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 9, width: "100%",
  paddingLeft: 42, paddingRight: 4, paddingTop: 4, paddingBottom: 4,
  border: "none", background: "none", cursor: "pointer",
};

export function MatrixTab({ phases, items, onOpen, mobile }: { phases: Phase[]; items: Item[]; onOpen: (i: Item) => void; mobile: boolean }) {
  // The matrix cross-cuts client-facing outputs (deliverables + milestones)
  // by phase (rows) and status (columns).
  const outputs = items.filter((i) => i.type === "deliverable" || i.type === "milestone");
  if (outputs.length === 0) {
    return <Empty>No deliverables or milestones yet. Set an item's type to Deliverable or Milestone to see it here.</Empty>;
  }
  const cell = (phaseId: string, status: string) => outputs.filter((i) => i.phase === phaseId && i.status === status);
  const cols = `190px repeat(${STATUS_ORDER.length}, minmax(160px, 1fr))`;

  return (
    <div style={{ maxWidth: "100%" }}>
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 12 }}>Deliverables &amp; milestones by phase and status. Click any item to open it.</div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: mobile ? 900 : 1080 }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, marginBottom: 8 }}>
            <div />
            {STATUS_ORDER.map((s) => (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 4px" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: STATUS_META[s].dot }} />
                <span style={{ fontSize: 12, fontWeight: "var(--fw-medium)", color: "var(--ink2)", whiteSpace: "nowrap" }}>{SHORT_STATUS[s]}</span>
              </div>
            ))}
          </div>
          {/* Rows */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {phases.map((ph) => {
              const rowCount = outputs.filter((i) => i.phase === ph.id).length;
              return (
                <div key={ph.id} style={{ display: "grid", gridTemplateColumns: cols, gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", minWidth: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: ph.color, flex: "none" }} />
                    <span style={{ fontSize: 13.5, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-tight)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ph.name}</span>
                    <span className="mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted2)", flex: "none" }}>{rowCount}</span>
                  </div>
                  {STATUS_ORDER.map((s) => {
                    const list = cell(ph.id, s);
                    const occupied = list.length > 0;
                    return (
                      <div key={s} style={{ minHeight: 74, borderRadius: 14, padding: 8, display: "flex", flexDirection: "column", gap: 6, background: occupied ? "var(--bg2)" : "transparent", border: occupied ? "none" : "1px solid var(--tint2)" }}>
                        {occupied ? list.map((it) => (
                          <button key={it.id} onClick={() => onOpen(it)} title={it.title} style={{ display: "flex", alignItems: "flex-start", gap: 6, textAlign: "left", border: "1px solid var(--line)", cursor: "pointer", background: "var(--surface)", borderRadius: 10, padding: "7px 9px", fontSize: 12, fontWeight: "var(--fw-medium)", color: "var(--ink2)", boxShadow: "var(--shadowCard)" }}>
                            <span style={{ color: TYPE_META[it.type].color, flex: "none", lineHeight: 1.3 }}>{TYPE_META[it.type].glyph}</span>
                            <span style={{ lineHeight: 1.3, textDecoration: it.status === "done" ? "line-through" : "none", color: it.status === "done" ? "var(--muted)" : "var(--ink2)" }}>{it.title}</span>
                          </button>
                        )) : (
                          <span style={{ margin: "auto", color: "var(--muted3)", fontSize: 13 }}>—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- shared ----------------------------------------------------------------

/**
 * The project's name, edited in place. Commits on Enter or blur; every surface
 * that shows a project name reads the same `projects` cache, so one write
 * re-renders the header, the sidebar tree, the Projects page and every select.
 */
function NameRow({ project, autoFocus, onCommit }: {
  project: Project; autoFocus?: boolean; onCommit: (name: string) => void;
}) {
  const [value, setValue] = useState(project.name);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { setValue(project.name); }, [project.id, project.name]);
  useEffect(() => { if (autoFocus) { ref.current?.focus(); ref.current?.select(); } }, [autoFocus]);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid var(--tint2)", paddingBottom: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 12.5, color: "var(--muted)", flex: "none" }}>Name</span>
      <input
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); onCommit(e.currentTarget.value.trim()); ref.current?.blur(); }
          if (e.key === "Escape") { e.preventDefault(); setValue(project.name); ref.current?.blur(); }
        }}
        onBlur={() => onCommit(value.trim())}
        placeholder="Project name"
        style={{
          flex: 1, minWidth: 0, textAlign: "right", padding: "4px 8px", borderRadius: 7,
          border: "1px solid transparent", background: "transparent",
          fontSize: 13, fontWeight: "var(--fw-title)", color: "var(--ink)",
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accentLine)"; e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.textAlign = "left"; }}
        onBlurCapture={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; e.currentTarget.style.textAlign = "right"; }}
      />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ ...card, padding: 24, textAlign: "center", fontSize: 13, color: "var(--muted2)" }}>{children}</div>;
}

const primaryBtn: CSSProperties = { padding: "8px 14px", borderRadius: 999, border: "none", background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 500, cursor: "pointer" };
const ghostBtn: CSSProperties = { padding: "7px 13px", borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer" };
const linkBtn: CSSProperties = { background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 12, padding: 0 };
const fileInput: CSSProperties = { minWidth: 0, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 12.5 };
